import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormI18n,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns the EntityForm configuration for Tenant create/edit forms.
 *
 * @param mode - Whether the form is for creating or editing a tenant.
 * @param tenantId - The tenant ID (required in edit mode for fetching data).
 */
export function getTenantFormConfig(
  mode: 'create' | 'edit',
  tenantId?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';

  return {
    id: isEdit ? createFormEditId('tenant', tenantId) : createFormCreateId('tenant'),

    endpoint: createFormEndpoint(
      isEdit,
      '/api/admin/tenants',
      `/api/admin/tenants/${tenantId}`,
      !!tenantId,
      ['slug', 'name', 'domain', 'settings', 'is_active'],
    ),

    fields: {
      slug: {
        type: 'text',
        label: 'Slug',
        placeholder: 'e.g. my-tenant',
        helperText: 'Lowercase letters, numbers, and hyphens only',
        order: 1,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 100, message: 'Slug must be at most 100 characters' },
          pattern: {
            value: '^[a-z0-9-]+$',
            message: 'Only lowercase letters, numbers, and hyphens are allowed',
          },
        },
      },
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. Acme Corporation',
        order: 2,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be at most 255 characters' },
        },
      },
      domain: {
        type: 'text',
        label: 'Domain',
        placeholder: 'e.g. tenant.example.com',
        order: 3,
        section: 'basic',
        validation: {
          max: {
            value: 255,
            message: 'Domain must be at most 255 characters',
          },
        },
      },
      settings: {
        type: 'textarea',
        label: 'Settings (JSON)',
        helperText: 'JSON object for tenant settings',
        order: 4,
        section: 'settings',
        options: {
          rows: 6,
          resize: 'vertical' as const,
        },
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        defaultValue: true,
        order: 5,
        section: 'settings',
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        order: 1,
      },
      {
        id: 'settings',
        title: 'Settings',
        order: 2,
      },
    ],

    actions: createFormActions('Tenant', isEdit, { alignment: 'right' }),

    layout: createFormLayout(),

    workflow: createFormWorkflow(),

    i18n: createFormI18n('Tenant', isEdit),
  };
}
